import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Alert from "../../../Common/Alert";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import TreeView, { ACCOUNT_TYPE_STYLES } from "../../../Common/TreeView";
import { ChartOfAccountStyles, money, num } from "./ChartOfAccountShared";

const ALL = "all";

const List = () => {
  const location = useLocation();
  const [receiveData, setReceiveData] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [accountType, setAccountType] = useState(ALL);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await Http.get(`/tree-chart-of-account-code`);
      setReceiveData(res.data.chart_of_accounts || []);
      setAccountTypes(
        res.data.account_types || Object.keys(ACCOUNT_TYPE_STYLES)
      );
    } catch (err) {
      console.error("Fetch error:", err);
      setError(
        "Could not load the chart of accounts. Please refresh or try again."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const showAlert = (message, type) => {
    setAlert({ message: message, type: type });
    setTimeout(() => setAlert(null), 4000);
  };

  useEffect(() => {
    if (!location.state) {
      return;
    }
    showAlert(location.state.message, location.state.type);
  }, [location]);

  // How many accounts (at any depth) belong to each statement class, so the
  // chips can show the spread without switching filters.
  const stats = useMemo(() => {
    const counts = { [ALL]: 0 };
    let roots = 0;
    let leaves = 0;
    let openingTotal = 0;

    const walk = (nodes, depth = 0) => {
      (nodes || []).forEach((node) => {
        counts[ALL] += 1;
        if (depth === 0) roots += 1;
        if (!node.children_recursive || node.children_recursive.length === 0) {
          leaves += 1;
        }
        openingTotal += num(node.balance);
        if (node.account_type) {
          counts[node.account_type] = (counts[node.account_type] || 0) + 1;
        }
        walk(node.children_recursive, depth + 1);
      });
    };

    walk(receiveData);
    return { counts, roots, leaves, openingTotal };
  }, [receiveData]);

  const typeCounts = stats.counts;

  // Keep a node when it matches, when an ancestor matched (so a matching parent
  // keeps its whole subtree), or when one of its descendants matches.
  const filterTree = (nodes, matcher, ancestorMatched = false) => {
    const result = [];

    (nodes || []).forEach((node) => {
      const currentMatches = matcher(node);
      const keepWholeSubtree = currentMatches || ancestorMatched;
      const children = filterTree(
        node.children_recursive,
        matcher,
        keepWholeSubtree
      );

      if (keepWholeSubtree || children.length > 0) {
        result.push({ ...node, children_recursive: children });
      }
    });

    return result;
  };

  const filteredData = useMemo(() => {
    let data = receiveData;

    if (accountType !== ALL) {
      data = filterTree(data, (node) => node.account_type === accountType);
    }

    const term = searchTerm.trim().toLowerCase();
    if (term) {
      data = filterTree(data, (node) => {
        const name =
          typeof node.name === "string" ? node.name.toLowerCase() : "";
        const code = node.code ? String(node.code).toLowerCase() : "";
        return name.includes(term) || code.includes(term);
      });
    }

    return data;
  }, [receiveData, searchTerm, accountType]);

  const typeLabel = (type) =>
    type === ALL ? "All types" : ACCOUNT_TYPE_STYLES[type]?.label || type;

  const filtered = searchTerm || accountType !== ALL;

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">Chart of Accounts</h5>
            <p className="text-muted mb-0 tx-12">
              The full account hierarchy, by statement class
            </p>
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary"
              onClick={fetchData}
              title="Reload data"
            >
              <i className="fa fa-refresh"></i>
            </button>
            <Link to="/create-chart-of-account" className="btn btn-primary">
              <i className="fa fa-plus-square mg-r-6"></i>New Account
            </Link>
          </div>
        </div>

        <div className="am-pagebody">
          <Alert alert={alert} />

          <div className="row mg-b-20">
            <div className="col-md-3 mg-b-10">
              <div className="coa-stat">
                <i className="fa fa-sitemap coa-stat-icon"></i>
                <span className="coa-stat-label">Total Accounts</span>
                <span className="coa-stat-value">{typeCounts[ALL] || 0}</span>
                <span className="coa-stat-sub">
                  {stats.roots} top-level {stats.roots === 1 ? "root" : "roots"}
                </span>
              </div>
            </div>
            <div className="col-md-3 mg-b-10">
              <div className="coa-stat">
                <i className="fa fa-file-text-o coa-stat-icon"></i>
                <span className="coa-stat-label">Postable Accounts</span>
                <span className="coa-stat-value">{stats.leaves}</span>
                <span className="coa-stat-sub">
                  Leaf accounts entries can be booked to
                </span>
              </div>
            </div>
            <div className="col-md-3 mg-b-10">
              <div className="coa-stat">
                <i className="fa fa-tags coa-stat-icon"></i>
                <span className="coa-stat-label">Statement Classes</span>
                <span className="coa-stat-value">{accountTypes.length}</span>
                <span className="coa-stat-sub">Asset, liability, equity…</span>
              </div>
            </div>
            <div className="col-md-3 mg-b-10">
              <div
                className={`coa-stat ${
                  stats.openingTotal < 0 ? "is-alert" : "is-ok"
                }`}
              >
                <i className="fa fa-balance-scale coa-stat-icon"></i>
                <span className="coa-stat-label">Combined Balance</span>
                <span className="coa-stat-value">
                  {money(stats.openingTotal)}
                </span>
                <span className="coa-stat-sub">Across every account</span>
              </div>
            </div>
          </div>

          <div className="coa-panel">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-filter me-2"></i>Find an Account
              </span>
              {filtered && (
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => {
                    setSearchTerm("");
                    setAccountType(ALL);
                  }}
                >
                  <i className="fa fa-times mg-r-6"></i>Clear filters
                </button>
              )}
            </div>
            <div className="coa-panel-body">
              <div className="row">
                <div className="col-md-6">
                  <label>Search</label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="fa fa-search text-muted"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by account name or code"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-6 mg-t-10 mg-md-t-0">
                  <label>Account Type</label>
                  <select
                    className="form-control"
                    value={accountType}
                    onChange={(e) => setAccountType(e.target.value)}
                  >
                    <option value={ALL}>
                      All account types ({typeCounts[ALL] || 0})
                    </option>
                    {accountTypes.map((type) => (
                      <option key={type} value={type}>
                        {typeLabel(type)} ({typeCounts[type] || 0})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Per-type chips: the whole spread at a glance, one click to filter. */}
              <div className="d-flex flex-wrap gap-2 mg-t-16 mt-3">
                {[ALL, ...accountTypes].map((type) => {
                  const active = accountType === type;
                  const color =
                    type === ALL
                      ? "#52566b"
                      : ACCOUNT_TYPE_STYLES[type]?.bg || "#52566b";

                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setAccountType(type)}
                      className={`coa-chip${active ? " is-active" : ""}`}
                      style={active ? { backgroundColor: color } : undefined}
                    >
                      <span
                        className="coa-chip-dot"
                        style={{ backgroundColor: color }}
                      ></span>
                      {typeLabel(type)}
                      <span className="coa-chip-count">
                        {typeCounts[type] || 0}
                      </span>
                    </button>
                  );
                })}
              </div>

              {filtered && (
                <div className="coa-hint mg-t-14">
                  <i className="fa fa-filter mg-r-6"></i>
                  Showing <strong>{typeLabel(accountType)}</strong>
                  {searchTerm && (
                    <>
                      {" "}
                      matching <strong>{searchTerm}</strong>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="coa-notice is-alert">
              <i className="fa fa-exclamation-triangle"></i>
              <span className="flex-grow-1">{error}</span>
              <button
                className="btn btn-outline-danger btn-sm"
                onClick={fetchData}
              >
                Retry
              </button>
            </div>
          )}

          {loading ? (
            <div className="coa-panel">
              <div className="coa-empty">
                <i className="fa fa-spinner fa-spin"></i>
                <h6>Loading chart of accounts</h6>
                <p className="mb-0 tx-12">
                  Please wait while the account tree is fetched.
                </p>
              </div>
            </div>
          ) : (
            <TreeView data={filteredData} actionTemplate={actionTemplate} />
          )}
        </div>
      </div>
    </div>
  );
};

const actionTemplate = (item) => {
  return (
    <div className="d-flex gap-1 justify-content-center coa-no-print">
      <Link
        to={{ pathname: "/edit-chart-of-account/" + item.id }}
        className="btn btn-outline-secondary btn-sm"
        title="Edit account"
      >
        <i className="fa fa-pencil" aria-hidden="true"></i>
      </Link>
      <Link
        to={{ pathname: "/chart-of-account-ledger/" + item.id }}
        className="btn btn-outline-secondary btn-sm"
        title="View ledger"
      >
        <i className="fa fa-list" aria-hidden="true"></i>
      </Link>
    </div>
  );
};

export default List;
